import React, { CSSProperties, memo, useCallback } from 'react';

import { Checkbox, CollapsibleBlock } from '@components/common';

import { ICheckboxGroup } from '@components/common/NestedCheckboxGroup/NestedCheckboxGroup.types';

import styles from './NestedCheckboxGroup.module.scss';

interface ICheckboxGroupProps {
	group: ICheckboxGroup;
	onGroupToggle: (value: string) => void;
	depth?: number;
	maxHeightOfNestedItems?: number;
	onCheckboxClick?: (checked: boolean, name: string) => void;
	disabled?: boolean;
}

const GROUP_INDENT_MULTIPLIER = 26;
const ITEM_INDENT_MULTIPLIER = 46;

const CheckboxGroup = ({
	depth = 0,
	disabled,
	group,
	maxHeightOfNestedItems,
	onCheckboxClick,
	onGroupToggle,
}: ICheckboxGroupProps) => {
	const indentStyleForGroup = { marginLeft: `${depth * GROUP_INDENT_MULTIPLIER}px` };
	const indentStyleForItem = { marginLeft: `${(depth || 1) * ITEM_INDENT_MULTIPLIER}px` };
	const nestedContainerStyle = maxHeightOfNestedItems ? {
		overflowY: 'auto' as CSSProperties['overflowY'],
		maxHeight: `${maxHeightOfNestedItems}px`,
	} : undefined;

	const handleToggleGroupState = useCallback(() => {
		onGroupToggle(group.value);
	}, [group.value, onGroupToggle]);

	return (
		<div style={indentStyleForGroup}>
			<CollapsibleBlock
				key={group.value}
				containerClassname={styles.checkboxGroupContainer}
				handleLabelClick={handleToggleGroupState}
				isClickableRow
				isLeftSideButton
				isOpen={!group.isCollapsed}
				label={
					<Checkbox
						checked={group.checked}
						color={group.color}
						disabled={disabled}
						isIndefinite={group.isIndefinite}
						label={group.label}
						name={group.value}
						onChange={onCheckboxClick}
					/>
				}
				name={group.value}
				toggleState={handleToggleGroupState}
				withSeparatorLine={depth === 0}
			>
				{group.nestedItems && !!group.nestedItems.length && (
					<div style={nestedContainerStyle}>
						{group.nestedItems.map((nestedItem) => {
							return nestedItem?.nestedItems?.length ? (
								<CheckboxGroup
									key={`${nestedItem.value}-${group.value}`}
									depth={depth + 1}
									disabled={disabled}
									group={nestedItem}
									onCheckboxClick={onCheckboxClick}
									onGroupToggle={onGroupToggle}
								/>
							) : (
								<div
									key={`${nestedItem.value}-${group.value}`}
									className={styles.checkboxItem}
									style={indentStyleForItem}
								>
									<Checkbox
										checked={nestedItem.checked}
										disabled={disabled}
										label={nestedItem.label}
										name={nestedItem.value}
										onChange={onCheckboxClick}
									/>
								</div>
							);
						})}
					</div>
				)}
			</CollapsibleBlock>
			{!group.isCollapsed && <div className={styles.divider} />}
		</div>
	);
};

export default memo(CheckboxGroup);
